import { AgentIcon } from "@promptlayer/ui";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";

interface GenerateWithAIPresetSetupProps {
  isSubmitting: boolean;
  onSubmit: (message: string) => void;
}

const GenerateWithAIPresetSetup = ({ isSubmitting, onSubmit }: GenerateWithAIPresetSetupProps) => {
  const [message, setMessage] = useState("");

  const trimmedMessage = message.trim();
  const canSubmit = trimmedMessage.length > 0;

  return (
    <div className="flex min-h-0 flex-col">
      <div className="max-h-[58vh] space-y-4 overflow-y-auto px-4 py-4 sm:px-5">
        <div className="space-y-1.5">
          <div className="flex items-start justify-between gap-3">
            <label className="text-sm font-medium text-gray-950" htmlFor="generate-with-ai-message">
              What should Wrangler AI build?
            </label>
            <span className="rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-400">
              Required
            </span>
          </div>
          <Textarea
            id="generate-with-ai-message"
            className="min-h-[150px] resize-none border-gray-200 bg-white text-sm shadow-none focus-visible:ring-1"
            value={message}
            onChange={event => setMessage(event.target.value)}
            placeholder="Example: Build a smart table to compare two support prompts across GPT-4o and Claude. Add a pass/fail quality check column and run it on 10 sample user questions."
            aria-invalid={!canSubmit}
          />
          {!canSubmit && <p className="text-xs text-red-600">Enter a request to continue.</p>}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-3">
          <div className="flex items-start gap-2.5">
            <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white text-black shadow-sm ring-1 ring-gray-200">
              <AgentIcon className="h-5 w-5" />
            </div>
            <p className="text-xs leading-5 text-gray-600">
              We’ll send your request to Wrangler AI in Vibe Chat so it can generate and run the
              table setup.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-gray-50/60 px-4 py-3 sm:px-5">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3">
          <p className="text-xs text-gray-500">
            {canSubmit ? "Ready to generate." : "Describe what you want Wrangler AI to create."}
          </p>
          <Button
            type="button"
            onClick={() => {
              if (!canSubmit) {
                return;
              }

              onSubmit(trimmedMessage);
            }}
            disabled={isSubmitting || !canSubmit}
          >
            {isSubmitting ? "Generating…" : "Generate with AI"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default GenerateWithAIPresetSetup;
